/**
 * What the console may say about a robot with no route, and about a task
 * refused because of one (lift rules, 2026-11-03).
 *
 * RMF names neither the lift nor a reason. `no_route_since_ms` says only that
 * it can find a task no way to its next leg, which has other causes than a
 * blocked lift. So this says "blocked lift" only while a lift scope at this
 * site really is closed, and names the lift only when exactly one is: with two
 * closed, nothing here can tell which one the robot is waiting for.
 *
 * Pure: the rules come in as data.
 */
import type { LiftRuleScope, LiftRulesView } from '../api/v1';

export interface ClosedLiftScope {
  readonly lift: string;
  readonly scope: LiftRuleScope;
}

/**
 * The scopes at this site that are closing lanes now, as the adapters report
 * applying them. Null when the rules cannot be read: nothing is known then,
 * which is not the same as nothing being closed, though neither allows the
 * words "blocked lift".
 *
 * A scope with no lanes is not closing anything: PL3's built-in Same floor only
 * reports `available: false`, and a robot's missing route has nothing to do
 * with it.
 */
export function closedLiftScopes(view: LiftRulesView | null | undefined): ClosedLiftScope[] | null {
  if (!view) return null;
  const closed: ClosedLiftScope[] = [];
  for (const lift of view.lifts) {
    if (!lift.commissioned) continue;
    for (const scope of [lift.use, lift.between_floors]) {
      if (scope.available && scope.applied === 'closed') {
        closed.push({ lift: lift.lift, scope: scope.scope });
      }
    }
  }
  return closed;
}

export interface NoRouteWords {
  /** True when this may be called waiting on a lift. */
  readonly liftBlocked: boolean;
  /** Fits on a line, lower case: "waiting while SL1 is blocked from robot use". */
  readonly short: string;
  /** One sentence, for a panel. */
  readonly sentence: string;
}

const NO_ROUTE: NoRouteWords = {
  liftBlocked: false,
  short: 'no route to its next stop',
  sentence: 'The system can’t find a way to its next stop right now.',
};

export function noRouteWords(closed: readonly ClosedLiftScope[] | null): NoRouteWords {
  if (!closed || closed.length === 0) return NO_ROUTE;
  const only = closed.length === 1 ? closed[0]! : null;
  if (!only) {
    return {
      liftBlocked: true,
      short: 'waiting while a lift is blocked',
      sentence:
        'Waiting while a lift is blocked. More than one is, and the system can’t say which one it needs. It carries on when its way is clear.',
    };
  }
  const what =
    only.scope === 'use'
      ? `${only.lift} is blocked from robot use`
      : `travel between floors is off on ${only.lift}`;
  return {
    liftBlocked: true,
    short: `waiting while ${what}`,
    sentence: `Waiting while ${what}. It carries on when the lift is released.`,
  };
}

/** A task's no-route state: only a task still running can have one. */
export function hasNoRoute(task: {
  readonly noRouteSinceMs?: number;
  readonly status?: string;
}): boolean {
  return (
    typeof task.noRouteSinceMs === 'number' &&
    task.status !== 'completed' &&
    task.status !== 'failed' &&
    task.status !== 'cancelled'
  );
}

// ----------------------------------------------------------------------------
// A task refused while lanes are closed (core, FleetUpdateHandle's lane check).
// ----------------------------------------------------------------------------

const TAKE_RE =
  /No robot in this fleet can take task_id \[[^\]]*\] while lanes are closed, because of the task in progress on((?: \[[^\]]+\])+)/;
const REACH_RE = /No robot in this fleet can reach task_id \[[^\]]*\] while lanes are closed/;

function listWords(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The refusal in plain words, or null when this is not one. The task has ended,
 * and nothing dispatches it again on its own (`is_retryable_failure`: an adapter
 * answered, so the worker does not retry), so the words say to start it again.
 */
export function plainLaneRefusal(text: string | null | undefined): string | null {
  if (!text) return null;
  const take = TAKE_RE.exec(text);
  if (take) {
    const names = [...take[1]!.matchAll(/\[([^\]]+)\]/g)].map((m) => m[1]!);
    const waits = names.length === 1 ? 'is' : 'are';
    return `${listWords(names)} ${waits} waiting for a blocked lift, so no robot could take this task. It can’t be taken until the lift is released; start it again then.`;
  }
  if (REACH_RE.test(text)) {
    return 'No robot can reach this task while a lift is blocked, so it did not start. It can’t be taken until the lift is released; start it again then.';
  }
  return null;
}
